import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { EASE, fmtPct, fmtUsd, pctColor, protocolAssetUrl, timeAgo, trackedSince } from "./nl";
import { AnimatedMetric } from "./AnimatedMetric";

/* Aave Entity Hero — Figma 1440 master (identity column + Protocol Pulse).
   Canonical rule: protocol displays as "Aave" (title case), never AAVE.
   Enter staging: identity ~0–220ms, metrics ~140–420ms (Motion Contract §1). */

const Readout = ({ value, raw, format, label, color = "#ffffff", i, testid }: {
  value?: string; raw?: number | null; format?: (n: number | null | undefined) => string;
  label: string; color?: string; i: number; testid: string;
}) => {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0.12 : 0.28, delay: reduced ? 0 : 0.14 + i * 0.055, ease: EASE }}
      className="flex flex-col gap-[3px]" data-testid={testid}>
      {raw != null && format ? (
        <AnimatedMetric value={raw} format={format} baseColor={color}
          className="nl-sg text-[24px] leading-[28px] font-medium tracking-[-0.01em]" />
      ) : (
        <span className="nl-sg text-[24px] leading-[28px] font-medium tracking-[-0.01em]" style={{ color }}>{value}</span>
      )}
      <span className="nl-geist text-[10px] leading-[14px] font-semibold tracking-[1px] uppercase text-[#9696a8]">{label}</span>
    </motion.div>
  );
};

export const EntityHero = ({ overview }: { overview: any }) => {
  const reduced = useReducedMotion();
  const [logoErr, setLogoErr] = useState(false);
  const id = overview.identity;
  const m = overview.metrics;
  const tok = overview.token;
  // Profile-driven pulse: DEX profiles lead with trading activity (24H VOLUME
  // is the hero); all other profiles keep the approved TVL-forward order.
  const dex = id.profile === "dex" && overview.dex_activity && !overview.dex_activity.error
    ? overview.dex_activity : null;
  const tokFmt = (n: number | null | undefined) =>
    n == null ? "—" : `$${n < 10 ? n.toFixed(3) : n.toFixed(2)}`;
  const stats: any[] = [];
  if (dex?.volume?.total_24h_usd != null) {
    stats.push({ raw: dex.volume.total_24h_usd, format: fmtUsd, label: "24H Trading Volume", testid: "pulse-volume" });
    if (dex.market_share?.share_pct != null) {
      stats.push({
        value: `${dex.market_share.rank_by_24h ? `#${dex.market_share.rank_by_24h} · ` : ""}${dex.market_share.share_pct}%`,
        label: "DEX Market Share", color: "#22d3ee", testid: "pulse-market-share",
      });
    }
    if (m) stats.push({ raw: m.tvl_usd, format: fmtUsd, label: "Total Value Locked", testid: "pulse-tvl" });
    if (m?.chain_count != null) stats.push({ value: String(m.chain_count), label: "Deployed Chains", color: "#8b5cff", testid: "pulse-chains" });
    if (tok?.price_usd != null) stats.push({ raw: tok.price_usd, format: tokFmt, label: `${tok.symbol} Token`, color: "#8b5cff", testid: "pulse-token" });
  } else {
    if (m) stats.push({ raw: m.tvl_usd, format: fmtUsd, label: "Total Value Locked", testid: "pulse-tvl" });
    if (m?.tvl_change_24h_pct != null) stats.push({ raw: m.tvl_change_24h_pct, format: (n: any) => fmtPct(n), label: "24H TVL", color: pctColor(m.tvl_change_24h_pct), testid: "pulse-24h" });
    if (m?.chain_count != null) stats.push({ value: String(m.chain_count), label: "Deployed Chains", color: "#8b5cff", testid: "pulse-chains" });
    if (m?.dominant_chain) stats.push({ value: m.dominant_chain, label: "Dominant Chain", color: "#22d3ee", testid: "pulse-dominant" });
    if (tok?.price_usd != null) stats.push({ raw: tok.price_usd, format: tokFmt, label: `${tok.symbol} Token`, color: "#8b5cff", testid: "pulse-token" });
  }
  const ident = { hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };
  return (
    <motion.div initial="hidden" animate="show"
      transition={{ duration: reduced ? 0.12 : 0.22, ease: EASE }} variants={ident}
      data-testid="entity-hero">
      <p className="nl-geist text-[8px] md:text-[10px] leading-[12px] md:leading-[14px] font-semibold tracking-[0.08em] md:tracking-[1px] text-[#9696a8] uppercase"
        data-testid="entity-breadcrumb">
        <Link to="/defi" className="hover:text-white transition-colors duration-200">SECTORS</Link>
        <span aria-hidden>&nbsp;&nbsp;/&nbsp;&nbsp;</span>
        <Link to="/defi" className="hover:text-white transition-colors duration-200">DEFI</Link>
        <span aria-hidden>&nbsp;&nbsp;/&nbsp;&nbsp;</span>
        <Link to="/defi/protocols" className="hover:text-white transition-colors duration-200">PROTOCOLS</Link>
        <span aria-hidden>&nbsp;&nbsp;/&nbsp;&nbsp;</span>
        <span className="text-white/80">{id.name.toUpperCase()}</span>
      </p>
      <div className="flex items-start gap-[22px] mt-[30px] md:mt-[44px]">
        <div className="w-[54px] h-[54px] md:w-[74px] md:h-[74px] rounded-[18px] md:rounded-[24px] bg-[#0e0e16] border border-[#8b5cff] grid place-items-center shrink-0 relative"
          data-testid="entity-identity-mark">
          <span className="absolute inset-[13px] md:inset-[14px] rounded-full border border-[#8b5cff] opacity-55" aria-hidden />
          {!logoErr ? (
            <img src={protocolAssetUrl(id.slug)} alt={`${id.name} official logo`}
              className="w-[34px] h-[34px] md:w-[46px] md:h-[46px] rounded-full relative z-10"
              onError={() => setLogoErr(true)} />
          ) : (
            <span className="nl-sg text-[25px] font-bold text-white relative z-10">{id.name[0]}</span>
          )}
        </div>
        <div className="min-w-0">
          <p className="nl-geist text-[10px] leading-[14px] font-semibold tracking-[1px] text-[#8b5cff] uppercase">
            ENTITY INTELLIGENCE&nbsp;&nbsp;·&nbsp;&nbsp;{id.type.toUpperCase()}
          </p>
          <h1 className="nl-sg text-[52px] leading-[52px] md:text-[78px] md:leading-[78px] font-bold tracking-[-0.02em] text-white mt-[6px]"
            data-testid="entity-title">{id.name}</h1>
          <p className="nl-geist text-[11px] leading-[15px] font-semibold tracking-[0.08em] text-[#9696a8] uppercase mt-[10px] ml-[2px]">
            {(id.category || "").toUpperCase()}{(m?.chain_count || 0) > 1 ? "  ·  MULTICHAIN" : ""}
          </p>
        </div>
      </div>
      {id.description && (
        <p className="nl-geist text-[15px] md:text-[16px] leading-[24px] md:leading-[26px] text-[#b9b9c7] mt-[36px] md:mt-[48px] max-w-[500px]"
          data-testid="entity-description">{id.description}</p>
      )}
      {id.tracked_since && (
        <p className="nl-mono text-[10px] leading-[15px] font-medium tracking-[0.02em] text-[#9696a8] mt-[22px]"
          data-testid="entity-tracked-since">
          TRACKED SINCE&nbsp;&nbsp;·&nbsp;&nbsp;{trackedSince(id.tracked_since)}
        </p>
      )}
      <div className="grid grid-cols-2 gap-y-[28px] md:flex md:flex-wrap md:gap-x-[22px] xl:gap-x-[26px] md:gap-y-[24px] mt-[42px] md:mt-[56px]" data-testid="protocol-pulse">
        {stats.map((s, i) => <Readout key={s.testid} i={i} {...s} />)}
      </div>
      {overview.updated_at && (
        <p className="nl-mono text-[9px] md:text-[10px] leading-[14px] font-medium text-[color:var(--nl-text-faint)] mt-[18px] uppercase"
          data-testid="pulse-freshness">
          {timeAgo(overview.updated_at)}&nbsp;&nbsp;·&nbsp;&nbsp;NORMALIZED DATA
        </p>
      )}
    </motion.div>
  );
};
